import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextAreaInput from '../../components/TextAreaInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import SelectInput from '../../components/SelectInput';
import leadApi from '../../api/leadApi';
import leadStatusApi from '../../api/leadStatusApi';
import { useToast } from '../../contexts/ToastContext';
import { format } from 'date-fns';

const EditLead = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    lead_status_id: '',
    comment: '',
  });
  const [leadFound, setLeadFound] = useState(true);
  const [leadHistory, setLeadHistory] = useState([]);

  useEffect(() => {
    const fetchLeadData = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const statusesResponse = await leadStatusApi.getLeadStatuses({ paginate: false });
        setLeadStatuses(statusesResponse.data && statusesResponse.data.data ? statusesResponse.data.data.map(status => ({
          value: status.id.toString(),
          label: status.name,
          color: status.color,
        })) : []);

        const leadResponse = await leadApi.getLeadByIdentifier(id);
        if (leadResponse.success && leadResponse.data) {
          const leadToEdit = leadResponse.data;
          setFormData({
            name: leadToEdit.name,
            email: leadToEdit.email,
            subject: leadToEdit.subject,
            message: leadToEdit.message,
            lead_status_id: leadToEdit.lead_status_id ? leadToEdit.lead_status_id.toString() : '',
            comment: '',
          });
          setLeadFound(true);

          const historyResponse = await leadApi.getLeadHistory(id);
          if (historyResponse.success && historyResponse.data) {
            setLeadHistory(historyResponse.data);
          } else {
            console.warn('EditLead: Failed to fetch lead history:', historyResponse.message);
            setLeadHistory([]);
          }

        } else {
          setLeadFound(false);
          setGeneralError(leadResponse.message || 'Lead not found.');
        }
      } catch (err) {
        console.error('EditLead: Failed to fetch lead data:', err);
        setLeadFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load lead. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadData();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleStatusChange = (e) => {
    const newStatusId = e.target.value;
    setFormData(prev => ({
      ...prev,
      lead_status_id: newStatusId,
    }));
    if (validationErrors.lead_status_id) {
      setValidationErrors(prev => ({ ...prev, lead_status_id: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = {
      lead_status_id: formData.lead_status_id ? parseInt(formData.lead_status_id, 10) : null,
      comment: formData.comment,
    };

    try {
      const response = await leadApi.updateLeadHistory(id, data);
      addToast('Lead history updated successfully!', 'success');
      setFormData(prev => ({ ...prev, comment: '' }));
      setLeadHistory(response.data.history);
    } catch (err) {
      console.error('Failed to update Lead history:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update Lead history. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!leadFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Lead Not Found</h1>
        <p className="text-text-gray">{generalError || 'The lead you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/contact-leads')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Leads List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Lead</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold text-text-white mb-4">Lead Details</h2>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm font-bold mb-2">Name</label>
              <p className="text-text-white">{formData.name}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm font-bold mb-2">Email</label>
              <p className="text-text-white">{formData.email}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm font-bold mb-2">Subject</label>
              <p className="text-text-white">{formData.subject}</p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm font-bold mb-2">Message</label>
              <p className="text-text-white whitespace-pre-wrap">{formData.message}</p>
            </div>

            <h2 className="text-xl font-semibold text-text-white mt-6 mb-4">Update Status & Comment</h2>
            <SelectInput
              label="Lead Status"
              name="lead_status_id"
              id="lead_status_id"
              value={formData.lead_status_id}
              onChange={handleStatusChange}
              options={leadStatuses}
              placeholder="Select Lead Status"
              error={validationErrors?.lead_status_id?.[0]}
            />
            <TextAreaInput
              label="Comment"
              name="comment"
              id="comment"
              value={formData.comment}
              onChange={handleChange}
              rows={3}
              placeholder="Add a comment for this lead..."
              error={validationErrors?.comment?.[0]}
            />
          </div>

          <div className="lg:max-h-[70vh] lg:overflow-y-auto">
            <h2 className="text-xl font-semibold text-text-white mb-4">Lead History</h2>
            {leadHistory.length > 0 ? (
              <div className="space-y-4">
                {leadHistory.map((entry, index) => (
                  <div key={entry.id || index} className="bg-gray-700 p-4 rounded-lg border border-gray-600">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-text-gray">
                        {format(new Date(entry.created_at), 'MMM dd, yyyy HH:mm')}
                      </span>
                      {entry.lead_status ? (
                        <span
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: entry.lead_status.color || '#6B7280', color: '#FFFFFF' }}
                        >
                          {entry.lead_status.name}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </div>
                    {entry.comment && <p className="text-text-white mb-2">{entry.comment}</p>}
                    {entry.user && (
                      <p className="text-xs text-text-gray">
                        By: {entry.user.first_name} {entry.user.last_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-gray">No history available for this lead.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update History
          </Button>
          <Button
            type="button"
            onClick={() => navigate('/contact-leads')}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditLead;